/**
 * Testnet Mock Provider
 *
 * Intercepts swap requests when isTestnet() is true and returns quotes
 * using live CoinGecko prices (with fallback to hardcoded rates).
 *
 * On testnet, quotes carry { __mock: true } in the route field.
 * At execution time, buildMockSteps() produces concrete transaction steps
 * (EVM calls to TestnetSwapPool or Canton Daml creates) that the frontend
 * executes sequentially via the Stratos SDK.
 *
 * On testnet ALL routes are intercepted — including CC ↔ USDC (Base),
 * CC ↔ cUSDC (Canton), and USDC-on-Canton aliases.
 */

import { isTestnet } from './networkConfig';
import {
  getPoolAddress,
  getTokenAddress,
  getChainId,
  isNativeETH,
  generateNonce,
  toRaw,
  toHexValue,
  encodeApprove,
  encodeSwap,
  encodeSwapETHForToken,
  encodeSwapTokenForETH,
  encodeBridgeLock,
  encodeBridgeLockETH,
  encodeBridgeRelease,
  encodeBridgeReleaseETH,
} from './testnetPoolConfig';
import { cantonBridgeApi } from './cantonBridgeApi';
import type { SwapQuoteRequest, SwapQuote } from './swapService';
import type { SwapProvider } from './swapRouter';

// ---------------------------------------------------------------------------
// Live rates from CoinGecko (cached for 60s, fallback to hardcoded)
// ---------------------------------------------------------------------------
const FALLBACK_RATES: Record<string, number> = {
  CC: 0.16,
  CUSDC: 1.0,
  USDC: 1.0,
  ETH: 1970,
};

// CoinGecko ID mapping for tokens we care about
const COINGECKO_IDS: Record<string, string> = {
  ETH: 'ethereum',
  USDC: 'usd-coin',
  CC: 'canton-network',
};

let _ratesCache: Record<string, number> = { ...FALLBACK_RATES };
let _ratesCacheTs = 0;
const CACHE_TTL_MS = 60_000; // 60 seconds
let _fetchInFlight: Promise<void> | null = null;

async function refreshRates(): Promise<void> {
  const ids = Object.values(COINGECKO_IDS).join(',');
  try {
    const res = await fetch(
      `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd`
    );
    if (!res.ok) throw new Error(`CoinGecko HTTP ${res.status}`);
    const data = await res.json();
    const updated: Record<string, number> = { ...FALLBACK_RATES };
    for (const [symbol, cgId] of Object.entries(COINGECKO_IDS)) {
      const price = data[cgId]?.usd;
      if (typeof price === 'number' && price > 0) {
        updated[symbol] = price;
      }
    }
    _ratesCache = updated;
    _ratesCacheTs = Date.now();
  } catch (err) {
    console.warn('[testnetMock] CoinGecko fetch failed, using cached/fallback rates:', err);
  }
}

async function ensureRates(): Promise<Record<string, number>> {
  if (Date.now() - _ratesCacheTs < CACHE_TTL_MS) return _ratesCache;
  if (!_fetchInFlight) {
    _fetchInFlight = refreshRates().finally(() => { _fetchInFlight = null; });
  }
  await _fetchInFlight;
  return _ratesCache;
}

// ---------------------------------------------------------------------------
// Mock route table
// ---------------------------------------------------------------------------
interface MockRouteDef {
  provider: SwapProvider;
  estimatedTime: number; // seconds
}

/**
 * Key format: "SYMBOL:CHAIN:SYMBOL:CHAIN" (all lower-case).
 *
 * Tokens: CC, ETH, USDC
 * Chains: Canton, Base, Ethereum
 *
 * CC only lives on Canton. ETH and USDC live on Base and Ethereum.
 * USDC on Canton = USDCHolding contract (same-chain swap with CC only).
 */
const MOCK_ROUTES: Record<string, MockRouteDef> = {
  // ---- Canton same-chain: CC <-> USDC (USDCHolding) ----
  'cc:canton:usdc:canton':      { provider: 'canton', estimatedTime: 30 },
  'usdc:canton:cc:canton':      { provider: 'canton', estimatedTime: 30 },

  // ---- Canton Bridge: CC <-> ETH/USDC on EVM chains ----
  'cc:canton:usdc:base':        { provider: 'canton', estimatedTime: 180 },
  'usdc:base:cc:canton':        { provider: 'canton', estimatedTime: 180 },
  'cc:canton:usdc:ethereum':    { provider: 'canton', estimatedTime: 300 },
  'usdc:ethereum:cc:canton':    { provider: 'canton', estimatedTime: 300 },
  'cc:canton:eth:base':         { provider: 'canton', estimatedTime: 180 },
  'eth:base:cc:canton':         { provider: 'canton', estimatedTime: 180 },
  'cc:canton:eth:ethereum':     { provider: 'canton', estimatedTime: 300 },
  'eth:ethereum:cc:canton':     { provider: 'canton', estimatedTime: 300 },

  // ---- EVM same-chain swaps ----
  'eth:base:usdc:base':         { provider: 'skipgo', estimatedTime: 15 },
  'usdc:base:eth:base':         { provider: 'skipgo', estimatedTime: 15 },
  'eth:ethereum:usdc:ethereum': { provider: 'skipgo', estimatedTime: 15 },
  'usdc:ethereum:eth:ethereum': { provider: 'skipgo', estimatedTime: 15 },

  // ---- EVM cross-chain bridge ----
  'eth:ethereum:eth:base':      { provider: 'skipgo', estimatedTime: 60 },
  'eth:base:eth:ethereum':      { provider: 'skipgo', estimatedTime: 60 },
  'usdc:ethereum:usdc:base':    { provider: 'skipgo', estimatedTime: 60 },
  'usdc:base:usdc:ethereum':    { provider: 'skipgo', estimatedTime: 60 },
  'eth:ethereum:usdc:base':     { provider: 'skipgo', estimatedTime: 60 },
  'usdc:base:eth:ethereum':     { provider: 'skipgo', estimatedTime: 60 },
};

function routeKey(fromSymbol: string, fromChain: string, toSymbol: string, toChain: string): string {
  return `${fromSymbol.toLowerCase()}:${fromChain.toLowerCase()}:${toSymbol.toLowerCase()}:${toChain.toLowerCase()}`;
}

interface MultiHopRoute {
  hop1Key: string;
  hop2Key: string;
  intermediateSym: string;
  intermediateChain: string;
}

function findMultiHopRoute(fromSym: string, fromChain: string, toSym: string, toChain: string): MultiHopRoute | null {
  // Try CC:Canton as intermediate hub
  const hop1 = routeKey(fromSym, fromChain, 'CC', 'canton');
  const hop2 = routeKey('CC', 'canton', toSym, toChain);
  if (hop1 in MOCK_ROUTES && hop2 in MOCK_ROUTES) {
    return { hop1Key: hop1, hop2Key: hop2, intermediateSym: 'CC', intermediateChain: 'canton' };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Step types for buildMockSteps
// ---------------------------------------------------------------------------

/** A single EVM transaction to send via sdk.sendEVMTransaction */
export interface EVMStep {
  type: 'evm';
  chainId: number;
  to: string;
  data: string;
  value: string;    // hex, e.g. "0x0" or "0xDE0B6B3A7640000"
  description: string;
}

/** A Canton Daml contract create via sdk.cantonCreate */
export interface CantonStep {
  type: 'cantonCreate';
  templateId: string;
  payload: Record<string, unknown>;
  description: string;
}

/** Splice CC transfer via sdk.transfer() — sends CC to BridgeOperator */
export interface SpliceTransferStep {
  type: 'spliceTransfer';
  to: string;        // BridgeOperator party (placeholder __BRIDGE_PARTY__)
  amount: string;    // Human-readable CC amount (e.g. "10.000000")
  symbol: string;    // 'CC'
  description: string;
}

/** Backend bridge processing — accept TransferOffer + exercise CompleteEscrow/ApproveAndRelease */
export interface BridgeProcessStep {
  type: 'bridgeProcess';
  description: string;
}

/** Backend CC release — create TransferOffer from BridgeOperator to user */
export interface BridgeReleaseCCStep {
  type: 'bridgeReleaseCC';
  amount: string;           // Human-readable CC amount
  recipientParty: string;   // Placeholder __PARTY__
  description: string;
}

/** Backend USDC release — transfer USDCHolding from BridgeOperator to user */
export interface BridgeReleaseUSDCStep {
  type: 'bridgeReleaseUSDC';
  amount: string;           // Human-readable USDC amount
  recipientParty: string;   // Placeholder __PARTY__
  description: string;
}

export type MockStep = EVMStep | CantonStep | SpliceTransferStep | BridgeProcessStep | BridgeReleaseCCStep | BridgeReleaseUSDCStep;

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/** Returns true when testnet mode is active AND the pair exists in the mock table. */
export function canMockHandle(req: SwapQuoteRequest): boolean {
  if (!isTestnet()) return false;
  const key = routeKey(req.fromSymbol, req.fromChain, req.toSymbol, req.toChain);
  if (key in MOCK_ROUTES) return true;
  return findMultiHopRoute(req.fromSymbol, req.fromChain, req.toSymbol, req.toChain) !== null;
}

/**
 * Router-level check: can we mock *any* route between these two chains?
 * Used to override "unsupported" verdicts in swapRouter when on testnet.
 */
export function canMockRoute(fromChain: string, toChain: string): boolean {
  if (!isTestnet()) return false;
  const from = fromChain.toLowerCase();
  const to = toChain.toLowerCase();
  for (const key of Object.keys(MOCK_ROUTES)) {
    const parts = key.split(':');
    if (parts[1] === from && parts[3] === to) return true;
  }
  // Check if multi-hop through Canton is possible
  if (from !== to) {
    const hasFromToCanton = Object.keys(MOCK_ROUTES).some(k => { const p = k.split(':'); return p[1] === from && p[3] === 'canton'; });
    const hasCantonToTo = Object.keys(MOCK_ROUTES).some(k => { const p = k.split(':'); return p[1] === 'canton' && p[3] === to; });
    if (hasFromToCanton && hasCantonToTo) return true;
  }
  return false;
}

/** Build a SwapQuote using live CoinGecko rates (falls back to hardcoded). */
export async function getMockQuote(req: SwapQuoteRequest): Promise<SwapQuote> {
  const key = routeKey(req.fromSymbol, req.fromChain, req.toSymbol, req.toChain);
  const def = MOCK_ROUTES[key];

  if (def) {
    // Direct route
    const rates = await ensureRates();
    const fromRate = rates[req.fromSymbol.toUpperCase()] ?? 1;
    const toRate = rates[req.toSymbol.toUpperCase()] ?? 1;
    const amountIn = parseFloat(req.amount) || 0;
    const amountOut = (amountIn * fromRate) / toRate;

    const fromDecimals = req.fromDecimals;
    const toDecimals = req.toDecimals;

    return {
      provider: def.provider,
      fromSymbol: req.fromSymbol,
      fromChain: req.fromChain,
      toSymbol: req.toSymbol,
      toChain: req.toChain,
      amountIn: req.amount,
      amountOut: amountOut.toFixed(6),
      amountInRaw: Math.floor(amountIn * Math.pow(10, fromDecimals)).toString(),
      amountOutRaw: Math.floor(amountOut * Math.pow(10, toDecimals)).toString(),
      priceImpact: '0.01',
      estimatedFees: '0.50',
      estimatedTime: def.estimatedTime,
      route: { __mock: true },
    };
  }

  // Multi-hop route
  const multiHop = findMultiHopRoute(req.fromSymbol, req.fromChain, req.toSymbol, req.toChain);
  if (!multiHop) {
    throw new Error(`No mock route for ${key}`);
  }

  // Chain the rates: from → CC → to
  const rates = await ensureRates();
  const fromRate = rates[req.fromSymbol.toUpperCase()] ?? 1;
  const ccRate = rates['CC'] ?? 1.25;
  const toRate = rates[req.toSymbol.toUpperCase()] ?? 1;
  const amountIn = parseFloat(req.amount) || 0;
  const ccAmount = (amountIn * fromRate) / ccRate;       // hop1 output
  const amountOut = (ccAmount * ccRate) / toRate;         // hop2 output

  const fromDecimals = req.fromDecimals;
  const toDecimals = req.toDecimals;

  return {
    provider: 'canton' as SwapProvider,
    fromSymbol: req.fromSymbol,
    fromChain: req.fromChain,
    toSymbol: req.toSymbol,
    toChain: req.toChain,
    amountIn: req.amount,
    amountOut: amountOut.toFixed(6),
    amountInRaw: Math.floor(amountIn * Math.pow(10, fromDecimals)).toString(),
    amountOutRaw: Math.floor(amountOut * Math.pow(10, toDecimals)).toString(),
    priceImpact: '0.02',
    estimatedFees: '1.00',
    estimatedTime: MOCK_ROUTES[multiHop.hop1Key].estimatedTime + MOCK_ROUTES[multiHop.hop2Key].estimatedTime,
    route: { __mock: true, __multiHop: multiHop },
  };
}

/** Type guard: returns true if the route object is a mock sentinel. */
export function isMockRoute(route: unknown): boolean {
  return route != null && typeof route === 'object' && (route as any).__mock === true;
}

// ---------------------------------------------------------------------------
// Step builder — produces concrete tx steps from a mock quote
// ---------------------------------------------------------------------------

/**
 * Build an ordered array of MockStep objects that the frontend loops through
 * to execute a testnet swap. Each step is either an EVM tx or a Canton create.
 *
 * Canton steps use two placeholders in the payload:
 *   __PARTY__        — replaced with the user's Canton party ID (owner/recipient)
 *   __BRIDGE_PARTY__ — replaced with the bridge operator party ID
 *
 * If no dedicated bridge party has been configured, __BRIDGE_PARTY__ falls
 * back to the user's own party (user-as-operator model for testnet).
 *
 * @param quote       The mock quote returned by getMockQuote()
 * @param userAddress The user's EVM address (for bridge dest / approvals)
 */
export function buildMockSteps(quote: SwapQuote, userAddress: string): MockStep[] {
  // Multi-hop: decompose into two single-hop step sequences
  const multiHop = (quote.route as any)?.__multiHop as MultiHopRoute | undefined;
  if (multiHop) {
    return buildMultiHopSteps(quote, multiHop, userAddress);
  }

  const from = quote.fromSymbol.toUpperCase();
  const to = quote.toSymbol.toUpperCase();
  const fromChain = quote.fromChain.toLowerCase();
  const toChain = quote.toChain.toLowerCase();
  const sameChain = fromChain === toChain;

  // Determine decimals (ETH/CC = 18, USDC/cUSDC = 6)
  const fromDecimals = (from === 'ETH' || from === 'CC') ? 18 : 6;
  const toDecimals = (to === 'ETH' || to === 'CC') ? 18 : 6;
  const amountInRaw = toRaw(quote.amountIn, fromDecimals);
  const amountOutRaw = toRaw(quote.amountOut, toDecimals);

  // ---- Same-chain EVM swaps ----
  if (sameChain && fromChain !== 'canton') {
    return buildSameChainSwapSteps(from, to, amountInRaw, amountOutRaw, fromChain);
  }

  // ---- Canton involved ----
  if (fromChain === 'canton' || toChain === 'canton') {
    return buildCantonSteps(from, to, amountInRaw, amountOutRaw, fromChain, toChain, userAddress);
  }

  // ---- Cross-chain EVM (bridge lock on source, bridge release on dest) ----
  return buildCrossChainBridgeSteps(from, to, amountInRaw, amountOutRaw, fromChain, toChain, userAddress);
}

// ---------------------------------------------------------------------------
// Same-chain swap step builders
// ---------------------------------------------------------------------------

function buildSameChainSwapSteps(
  from: string, to: string,
  amountIn: bigint, amountOut: bigint,
  chain: string
): MockStep[] {
  const chainId = getChainId(chain);
  const pool = getPoolAddress(chainId);
  if (!pool) throw new Error(`No pool deployed on ${chain} (chainId ${chainId})`);

  const steps: MockStep[] = [];

  if (isNativeETH(from) && !isNativeETH(to)) {
    // ETH → ERC20: swapETHForToken
    const tokenOut = getTokenAddress(to, chainId);
    steps.push({
      type: 'evm',
      chainId,
      to: pool,
      data: encodeSwapETHForToken(tokenOut, amountOut),
      value: toHexValue(amountIn),
      description: `Swap ${from} for ${to} on ${chain}`,
    });
  } else if (!isNativeETH(from) && isNativeETH(to)) {
    // ERC20 → ETH: approve + swapTokenForETH
    const tokenIn = getTokenAddress(from, chainId);
    steps.push({
      type: 'evm',
      chainId,
      to: tokenIn,
      data: encodeApprove(pool, amountIn),
      value: '0x0',
      description: `Approve ${from} for pool`,
    });
    steps.push({
      type: 'evm',
      chainId,
      to: pool,
      data: encodeSwapTokenForETH(tokenIn, amountIn, amountOut),
      value: '0x0',
      description: `Swap ${from} for ${to} on ${chain}`,
    });
  } else {
    // ERC20 → ERC20: approve + swap
    const tokenIn = getTokenAddress(from, chainId);
    const tokenOut = getTokenAddress(to, chainId);
    steps.push({
      type: 'evm',
      chainId,
      to: tokenIn,
      data: encodeApprove(pool, amountIn),
      value: '0x0',
      description: `Approve ${from} for pool`,
    });
    steps.push({
      type: 'evm',
      chainId,
      to: pool,
      data: encodeSwap(tokenIn, amountIn, tokenOut, amountOut),
      value: '0x0',
      description: `Swap ${from} for ${to} on ${chain}`,
    });
  }

  return steps;
}

// ---------------------------------------------------------------------------
// Cross-chain EVM bridge step builders
// ---------------------------------------------------------------------------

function buildCrossChainBridgeSteps(
  from: string, to: string,
  amountIn: bigint, amountOut: bigint,
  fromChain: string, toChain: string,
  userAddress: string
): MockStep[] {
  const srcChainId = getChainId(fromChain);
  const dstChainId = getChainId(toChain);
  const srcPool = getPoolAddress(srcChainId);
  const dstPool = getPoolAddress(dstChainId);
  if (!srcPool) throw new Error(`No pool deployed on ${fromChain}`);
  if (!dstPool) throw new Error(`No pool deployed on ${toChain}`);

  const nonce = generateNonce();
  const steps: MockStep[] = [];

  // Step 1: Lock on source chain
  if (isNativeETH(from)) {
    steps.push({
      type: 'evm',
      chainId: srcChainId,
      to: srcPool,
      data: encodeBridgeLockETH(dstChainId, userAddress, nonce),
      value: toHexValue(amountIn),
      description: `Lock ${from} on ${fromChain} for bridge`,
    });
  } else {
    const tokenIn = getTokenAddress(from, srcChainId);
    steps.push({
      type: 'evm',
      chainId: srcChainId,
      to: tokenIn,
      data: encodeApprove(srcPool, amountIn),
      value: '0x0',
      description: `Approve ${from} for bridge lock`,
    });
    steps.push({
      type: 'evm',
      chainId: srcChainId,
      to: srcPool,
      data: encodeBridgeLock(tokenIn, amountIn, dstChainId, userAddress, nonce),
      value: '0x0',
      description: `Lock ${from} on ${fromChain} for bridge`,
    });
  }

  // Step 2: Release on destination chain (owner-only — user IS owner on testnet)
  if (isNativeETH(to)) {
    steps.push({
      type: 'evm',
      chainId: dstChainId,
      to: dstPool,
      data: encodeBridgeReleaseETH(amountOut, userAddress, nonce),
      value: '0x0',
      description: `Release ${to} on ${toChain} from bridge`,
    });
  } else {
    const tokenOut = getTokenAddress(to, dstChainId);
    steps.push({
      type: 'evm',
      chainId: dstChainId,
      to: dstPool,
      data: encodeBridgeRelease(tokenOut, amountOut, userAddress, nonce),
      value: '0x0',
      description: `Release ${to} on ${toChain} from bridge`,
    });
  }

  return steps;
}

// ---------------------------------------------------------------------------
// Canton <-> EVM step builders
//
// Delegates to cantonBridgeApi.buildEscrowPayload() and
// cantonBridgeApi.buildBridgeRequestPayload() so there is a single source
// of truth for Canton contract creation.
// ---------------------------------------------------------------------------

function buildCantonSteps(
  from: string, to: string,
  amountIn: bigint, amountOut: bigint,
  fromChain: string, toChain: string,
  userAddress: string
): MockStep[] {
  const steps: MockStep[] = [];
  const nonce = cantonBridgeApi.generateNonce();

  // ---- Canton to Canton (same-chain swap, e.g. CC <-> USDC on Canton) ----
  if (fromChain === 'canton' && toChain === 'canton') {
    const fromDec = (from === 'CC' || from === 'ETH') ? 18 : 6;
    const toDec = (to === 'CC' || to === 'ETH') ? 18 : 6;
    const humanIn = (Number(amountIn) / Math.pow(10, fromDec)).toFixed(6);
    const humanOut = (Number(amountOut) / Math.pow(10, toDec)).toFixed(6);

    if (from === 'CC') {
      // CC → USDC (Canton same-chain): 3 steps
      //   1. spliceTransfer: User sends CC to BridgeOperator via Splice
      //   2. bridgeProcess:  Backend accepts CC TransferOffer
      //   3. bridgeReleaseUSDC: Backend sends USDC to user
      steps.push({
        type: 'spliceTransfer',
        to: '__BRIDGE_PARTY__',
        amount: humanIn,
        symbol: 'CC',
        description: `Send ${humanIn} CC to Bridge Operator`,
      });
      steps.push({
        type: 'bridgeProcess',
        description: `Bridge processing (accept CC transfer)`,
      });
      steps.push({
        type: 'bridgeReleaseUSDC',
        amount: humanOut,
        recipientParty: '__PARTY__',
        description: `Bridge sends ${humanOut} USDC to your wallet`,
      });
    } else {
      // USDC → CC (Canton same-chain): 2 steps
      //   1. spliceTransfer(USDC): User sends USDC to BridgeOperator via canton-transfer
      //   2. bridgeReleaseCC: Backend creates CC TransferOffer + auto-accepts for user
      steps.push({
        type: 'spliceTransfer',
        to: '__BRIDGE_PARTY__',
        amount: humanIn,
        symbol: 'USDC',
        description: `Send ${humanIn} USDC to Bridge Operator`,
      });
      steps.push({
        type: 'bridgeReleaseCC',
        amount: humanOut,
        recipientParty: '__PARTY__',
        description: `Bridge sends ${humanOut} CC to your wallet`,
      });
    }

    return steps;
  }

  if (fromChain === 'canton') {
    // Canton → EVM: production-like 4-step flow
    //   1. spliceTransfer: User sends CC to BridgeOperator via Splice
    //   2. cantonCreate:   Create BridgeEscrow record
    //   3. bridgeProcess:  Backend accepts TransferOffer + completes escrow
    //   4. evm:            Release ETH/USDC from pool
    const evmChain = toChain;
    const evmChainId = getChainId(evmChain);
    const pool = getPoolAddress(evmChainId);
    if (!pool) throw new Error(`No pool deployed on ${evmChain}`);

    const fromDec = (from === 'CC' || from === 'ETH') ? 18 : 6;
    const humanAmountIn = (Number(amountIn) / Math.pow(10, fromDec)).toFixed(6);

    // Step 1: Splice transfer — send CC to BridgeOperator
    steps.push({
      type: 'spliceTransfer',
      to: '__BRIDGE_PARTY__',
      amount: humanAmountIn,
      symbol: 'CC',
      description: `Send ${humanAmountIn} CC to Bridge Operator`,
    });

    // Step 2: Canton escrow record
    const escrow = cantonBridgeApi.buildEscrowPayload({
      ownerParty: '__PARTY__',
      bridgeOperatorParty: '__BRIDGE_PARTY__',
      amount: humanAmountIn,
      destinationChain: evmChain,
      destinationAddress: userAddress,
      nonce,
    });
    steps.push({
      type: 'cantonCreate',
      templateId: escrow.templateId,
      payload: escrow.payload,
      description: `Create BridgeEscrow on Canton`,
    });

    // Step 3: Bridge processing (backend accepts CC TransferOffer + completes escrow)
    steps.push({
      type: 'bridgeProcess',
      description: `Bridge processing (accept CC + complete escrow)`,
    });

    // Step 4: Release on EVM chain
    if (isNativeETH(to)) {
      steps.push({
        type: 'evm',
        chainId: evmChainId,
        to: pool,
        data: encodeBridgeReleaseETH(amountOut, userAddress, nonce),
        value: '0x0',
        description: `Release ${to} on ${evmChain} from bridge`,
      });
    } else {
      const tokenOut = getTokenAddress(to, evmChainId);
      steps.push({
        type: 'evm',
        chainId: evmChainId,
        to: pool,
        data: encodeBridgeRelease(tokenOut, amountOut, userAddress, nonce),
        value: '0x0',
        description: `Release ${to} on ${evmChain} from bridge`,
      });
    }
  } else {
    // EVM → Canton: 4-step flow
    //   1. evm (approve if ERC20) + evm (lock): Lock ETH/USDC in pool
    //   2. cantonCreate:       Create BridgeRequest record
    //   3. bridgeProcess:      Backend exercises ApproveAndRelease
    //   4. bridgeReleaseCC:    Backend creates CC TransferOffer + auto-accepts for user
    const evmChain = fromChain;
    const evmChainId = getChainId(evmChain);
    const pool = getPoolAddress(evmChainId);
    if (!pool) throw new Error(`No pool deployed on ${evmChain}`);

    // Step 1: Lock on EVM
    if (isNativeETH(from)) {
      steps.push({
        type: 'evm',
        chainId: evmChainId,
        to: pool,
        data: encodeBridgeLockETH(0, userAddress, nonce), // destChainId=0 for Canton
        value: toHexValue(amountIn),
        description: `Lock ${from} on ${evmChain} for Canton bridge`,
      });
    } else {
      const tokenIn = getTokenAddress(from, evmChainId);
      steps.push({
        type: 'evm',
        chainId: evmChainId,
        to: tokenIn,
        data: encodeApprove(pool, amountIn),
        value: '0x0',
        description: `Approve ${from} for bridge lock`,
      });
      steps.push({
        type: 'evm',
        chainId: evmChainId,
        to: pool,
        data: encodeBridgeLock(tokenIn, amountIn, 0, userAddress, nonce),
        value: '0x0',
        description: `Lock ${from} on ${evmChain} for Canton bridge`,
      });
    }

    // Step 2: Canton bridge request
    const fromDecimalsLocal = from === 'ETH' ? 18 : 6;
    const toDecimalsLocal = (to === 'CC' || to === 'ETH') ? 18 : 6;
    const humanAmountIn = (Number(amountIn) / Math.pow(10, fromDecimalsLocal)).toFixed(6);
    const humanAmountOut = (Number(amountOut) / Math.pow(10, toDecimalsLocal)).toFixed(6);
    const request = cantonBridgeApi.buildBridgeRequestPayload({
      recipientParty: '__PARTY__',
      bridgeOperatorParty: '__BRIDGE_PARTY__',
      evmSourceAddress: userAddress,
      evmTxHash: '__PENDING__',
      usdcAmount: humanAmountIn,
      requestedCCAmount: humanAmountOut,
      nonce,
    });
    steps.push({
      type: 'cantonCreate',
      templateId: request.templateId,
      payload: request.payload,
      description: `Create BridgeRequest on Canton`,
    });

    // Step 3: Bridge processing (backend exercises ApproveAndRelease)
    steps.push({
      type: 'bridgeProcess',
      description: `Bridge processing (approve and release)`,
    });

    // Step 4: Bridge releases CC to user (backend creates TransferOffer)
    steps.push({
      type: 'bridgeReleaseCC',
      amount: humanAmountOut,
      recipientParty: '__PARTY__',
      description: `Bridge sends ${humanAmountOut} CC to your wallet`,
    });
  }

  return steps;
}

// ---------------------------------------------------------------------------
// Multi-hop step builder — decomposes a 2-hop route into two single-hop quotes
// and concatenates their step arrays.
// ---------------------------------------------------------------------------

function buildMultiHopSteps(
  quote: SwapQuote,
  multiHop: MultiHopRoute,
  userAddress: string,
): MockStep[] {
  const rates = { ..._ratesCache };
  const fromRate = rates[quote.fromSymbol.toUpperCase()] ?? 1;
  const ccRate = rates['CC'] ?? 1.25;
  const toRate = rates[quote.toSymbol.toUpperCase()] ?? 1;

  const amountIn = parseFloat(quote.amountIn) || 0;
  const ccAmount = (amountIn * fromRate) / ccRate;
  const amountOut = (ccAmount * ccRate) / toRate;

  const ccDecimals = 18;
  const fromDecimals = (quote.fromSymbol.toUpperCase() === 'ETH' || quote.fromSymbol.toUpperCase() === 'CC') ? 18 : 6;
  const toDecimals = (quote.toSymbol.toUpperCase() === 'ETH' || quote.toSymbol.toUpperCase() === 'CC') ? 18 : 6;

  // Build hop1 quote: from → CC:Canton
  const hop1Quote: SwapQuote = {
    provider: MOCK_ROUTES[multiHop.hop1Key].provider,
    fromSymbol: quote.fromSymbol,
    fromChain: quote.fromChain,
    toSymbol: multiHop.intermediateSym,
    toChain: multiHop.intermediateChain,
    amountIn: quote.amountIn,
    amountOut: ccAmount.toFixed(6),
    amountInRaw: Math.floor(amountIn * Math.pow(10, fromDecimals)).toString(),
    amountOutRaw: Math.floor(ccAmount * Math.pow(10, ccDecimals)).toString(),
    priceImpact: '0.01',
    estimatedFees: '0.50',
    estimatedTime: MOCK_ROUTES[multiHop.hop1Key].estimatedTime,
    route: { __mock: true },
  };

  // Build hop2 quote: CC:Canton → to
  const hop2Quote: SwapQuote = {
    provider: MOCK_ROUTES[multiHop.hop2Key].provider,
    fromSymbol: multiHop.intermediateSym,
    fromChain: multiHop.intermediateChain,
    toSymbol: quote.toSymbol,
    toChain: quote.toChain,
    amountIn: ccAmount.toFixed(6),
    amountOut: amountOut.toFixed(6),
    amountInRaw: Math.floor(ccAmount * Math.pow(10, ccDecimals)).toString(),
    amountOutRaw: Math.floor(amountOut * Math.pow(10, toDecimals)).toString(),
    priceImpact: '0.01',
    estimatedFees: '0.50',
    estimatedTime: MOCK_ROUTES[multiHop.hop2Key].estimatedTime,
    route: { __mock: true },
  };

  // Recursion is safe — each hop is a direct route (no __multiHop in route)
  const hop1Steps = buildMockSteps(hop1Quote, userAddress);
  const hop2Steps = buildMockSteps(hop2Quote, userAddress);

  return [...hop1Steps, ...hop2Steps];
}
